import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Upload, FileText, Eye, Trash2, Sparkles, CheckCircle,
  AlertTriangle, ChevronDown, ChevronUp, Info, XCircle, HelpCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  purchase_agreement: "Purchase Agreement",
  bank_statements: "Bank Statements",
  bank_statement: "Bank Statement",
  identity: "Government ID",
  entity_docs: "Entity Documents",
  scope_of_work: "Scope of Work / Renovation Plan",
  contractor_bids: "Contractor Bids",
  rent_roll: "Rent Roll / Financials",
  lease_agreements: "Lease Agreements",
  property_photos: "Property Photos",
  renovation_plan: "Renovation Plan / SOW",
  credit_application: "Credit Application",
  insurance: "Insurance",
  appraisal: "Appraisal",
  tax_returns: "Tax Returns",
  title_report: "Title Report",
  miscellaneous: "Miscellaneous",
};

const PHOTO_CATEGORIES = new Set(["photo", "property_photos", "photos", "exterior", "interior"]);

const formatSize = (bytes) => {
  if (!bytes) return "--";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

function getAuthHeaders() {
  const token = localStorage.getItem("ic_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STATUS_CONFIG = {
  uploaded: { label: "Uploaded", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  approved: { label: "Approved", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  missing: { label: "Missing", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  rejected: { label: "Resubmit", icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
};

export function DocumentsTab({ leadId }) {
  const [docs, setDocs] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("purchase_agreement");
  const [analyzing, setAnalyzing] = useState(null);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(null);

  const fetchDocs = useCallback(() => {
    axios.get(`${API}/borrowers/${leadId}/documents`, { headers: getAuthHeaders() })
      .then(r => setDocs(r.data.documents || []))
      .catch(() => {
        axios.get(`${API}/documents/${leadId}`, { headers: getAuthHeaders() })
          .then(r => setDocs(r.data.documents || []))
          .catch(() => {});
      });
  }, [leadId]);

  const fetchChecklist = useCallback(() => {
    axios.get(`${API}/borrower/${leadId}/doc-checklist`, { headers: getAuthHeaders() })
      .then(r => setChecklist(r.data))
      .catch(() => {});
  }, [leadId]);

  useEffect(() => { fetchDocs(); fetchChecklist(); }, [fetchDocs, fetchChecklist]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(
          `${API}/borrowers/${leadId}/documents?category=${category}`,
          formData,
          { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
        );
      }
      fetchDocs();
      fetchChecklist();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeaders() });
      fetchDocs();
      fetchChecklist();
    } catch (err) { console.error(err); }
  };

  const handleAnalyze = async (docId) => {
    setAnalyzing(docId);
    try {
      const res = await axios.post(`${API}/documents/${docId}/analyze`, {}, { headers: getAuthHeaders() });
      setDocs(prev => prev.map(d => d.doc_id === docId ? { ...d, ai_analysis: res.data.analysis } : d));
      setExpandedDoc(docId);
    } catch (err) {
      alert("Analysis failed: " + (err.response?.data?.detail || "Unknown error"));
    } finally {
      setAnalyzing(null);
    }
  };

  // Group docs by category — EXCLUDING photos
  const grouped = {};
  let docOnlyCount = 0;
  for (const d of docs) {
    const cat = d.category || d.doc_type || "miscellaneous";
    if (PHOTO_CATEGORIES.has(cat) || d.is_image) continue;
    (grouped[cat] = grouped[cat] || []).push(d);
    docOnlyCount++;
  }

  const checklistItems = checklist?.checklist || [];
  const summary = checklist?.summary || {};

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="documents-tab">
      {/* Document Checklist Progress */}
      {checklistItems.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="doc-checklist-panel">
          <div className="px-4 py-3 bg-[#faf7f2] border-b border-[#e8e0d0] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-bold text-[#0A0A0A]">Document Checklist</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono font-bold text-emerald-600">{summary.uploaded || 0}/{summary.total_required || 0}</span>
              <div className="w-20 h-1.5 rounded-full bg-[#EDE8DC] overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${summary.completion_pct || 0}%` }} />
              </div>
              <span className="text-[10px] font-bold text-[#888]">{summary.completion_pct || 0}%</span>
            </div>
          </div>
          <div className="divide-y divide-[#f0ebe0]">
            {checklistItems.filter(c => c.required).map((item) => {
              const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.missing;
              const StatusIcon = sc.icon;
              return (
                <div key={item.category} className="px-4 py-3 flex items-center gap-3" data-testid={`checklist-item-${item.category}`}>
                  <StatusIcon className={`h-4 w-4 shrink-0 ${sc.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-[#0A0A0A]">{item.label}</span>
                      {item.tooltip && (
                        <button
                          onClick={() => setTooltipOpen(tooltipOpen === item.category ? null : item.category)}
                          className="text-[#bbb] hover:text-[#666] transition-colors"
                          data-testid={`tooltip-trigger-${item.category}`}
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <span className={`ml-auto px-2 py-0.5 rounded-md text-[9px] font-bold border ${sc.bg}`}>
                        {sc.label}
                      </span>
                    </div>
                    {/* Tooltip */}
                    <AnimatePresence>
                      {tooltipOpen === item.category && item.tooltip && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-[10px] text-[#888] mt-1 leading-relaxed"
                          data-testid={`tooltip-content-${item.category}`}
                        >
                          {item.tooltip}
                        </motion.p>
                      )}
                    </AnimatePresence>
                    {/* Rejection reasons */}
                    {item.rejection_reasons?.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {item.rejection_reasons.map((reason, ri) => (
                          <div key={ri} className="flex items-start gap-1.5 text-[10px] text-red-600">
                            <XCircle className="h-3 w-3 shrink-0 mt-0.5" />
                            <span>{reason}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {item.status === "missing" && (
                    <label className="cursor-pointer shrink-0" data-testid={`checklist-upload-${item.category}`}>
                      <input type="file" className="hidden"
                        onChange={(e) => { setCategory(item.category); handleUpload(e); }}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.csv,.heic"
                        disabled={uploading} multiple />
                      <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white text-[10px] font-bold hover:bg-emerald-600 transition-colors">
                        <Upload className="h-3 w-3" /> Upload
                      </span>
                    </label>
                  )}
                  {item.status === "rejected" && (
                    <label className="cursor-pointer shrink-0" data-testid={`checklist-reupload-${item.category}`}>
                      <input type="file" className="hidden"
                        onChange={(e) => { setCategory(item.category); handleUpload(e); }}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.csv,.heic"
                        disabled={uploading} multiple />
                      <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-600 text-white text-[10px] font-bold hover:bg-red-500 transition-colors">
                        <Upload className="h-3 w-3" /> Resubmit
                      </span>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload bar */}
      <div className="flex items-center gap-3 flex-wrap p-3 rounded-xl border border-[#e8e0d0] bg-[#faf7f2]">
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="text-xs border border-[#e8e0d0] rounded-lg px-3 py-2 bg-white text-[#222] focus:outline-none focus:ring-1 focus:ring-emerald-400"
          data-testid="doc-category-select">
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <label className="cursor-pointer" data-testid="upload-doc-btn">
          <input type="file" className="hidden" onChange={handleUpload}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.csv,.heic"
            disabled={uploading} multiple />
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? "Uploading..." : "Upload Document"}
          </span>
        </label>
        <span className="text-[10px] text-[#999]">{docOnlyCount} document{docOnlyCount !== 1 ? "s" : ""} uploaded</span>
      </div>

      {/* Documents list grouped by category */}
      {docs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#e8e0d0] p-8 text-center">
          <Upload className="h-10 w-10 text-[#ddd] mx-auto mb-3" />
          <p className="text-sm text-[#888] mb-1">No documents uploaded yet</p>
          <p className="text-xs text-[#aaa]">Upload entity docs, contracts, bank statements, and other legal/financial files</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, catDocs]) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold text-[#888] uppercase tracking-wider">
                  {CATEGORY_LABELS[cat] || cat}
                </span>
                <span className="text-[10px] text-[#bbb]">({catDocs.length})</span>
              </div>
              <div className="space-y-2">
                {catDocs.map((doc) => (
                  <div key={doc.doc_id}>
                    <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between rounded-xl border border-[#e8e0d0] bg-white p-3 hover:border-emerald-200 transition-colors group">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-5 w-5 text-[#999] shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-[#0A0A0A] truncate" data-testid={`doc-name-${doc.doc_id}`}>{doc.original_filename || doc.filename}</div>
                          <div className="text-xs text-[#999] flex gap-2 items-center">
                            <span>{formatSize(doc.size)}</span>
                            {doc.ai_analysis && <span className="text-emerald-600 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Analyzed</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleAnalyze(doc.doc_id)} disabled={analyzing === doc.doc_id}
                          data-testid={`analyze-doc-${doc.doc_id}`}
                          className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors" title="AI Analyze">
                          {analyzing === doc.doc_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        </button>
                        {doc.ai_analysis && (
                          <button onClick={() => setExpandedDoc(expandedDoc === doc.doc_id ? null : doc.doc_id)}
                            className="p-1.5 rounded-lg text-[#888] hover:text-[#0A0A0A] hover:bg-[#faf7f2] transition-colors">
                            {expandedDoc === doc.doc_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        )}
                        <a href={`${API}/documents/download/${doc.doc_id}?auth=${localStorage.getItem("ic_token")}`}
                          target="_blank" rel="noopener noreferrer" data-testid={`preview-doc-${doc.doc_id}`}
                          className="p-1.5 rounded-lg text-[#aaa] hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <Eye className="h-4 w-4" />
                        </a>
                        <button onClick={() => handleDelete(doc.doc_id)} data-testid={`delete-doc-${doc.doc_id}`}
                          className="p-1.5 rounded-lg text-[#aaa] hover:text-red-600 hover:bg-red-50 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </motion.div>
                    <AnimatePresence>
                      {doc.ai_analysis && expandedDoc === doc.doc_id && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                          className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-3 overflow-hidden">
                          <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            <Sparkles className="h-3.5 w-3.5" /> AI Document Analysis
                          </div>
                          {doc.ai_analysis.summary && (
                            <p className="text-sm text-[#333] leading-relaxed">{doc.ai_analysis.summary}</p>
                          )}
                          {doc.ai_analysis.extracted_data?.key_figures?.length > 0 && (
                            <div>
                              <div className="text-xs uppercase text-[#888] mb-2">Key Figures</div>
                              <div className="grid gap-2 sm:grid-cols-2">
                                {doc.ai_analysis.extracted_data.key_figures.map((f, i) => (
                                  <div key={i} className="rounded-lg border border-[#e8e0d0] bg-white p-2">
                                    <div className="text-xs text-[#888]">{f.label}</div>
                                    <div className="text-sm font-bold text-[#0A0A0A]">{f.value}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {doc.ai_analysis.underwriting_notes?.length > 0 && (
                            <div>
                              <div className="text-xs uppercase text-[#888] mb-2">Underwriting Notes</div>
                              {doc.ai_analysis.underwriting_notes.map((n, i) => (
                                <div key={i} className="text-sm text-[#333] flex gap-2 mb-1"><CheckCircle className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /> {n}</div>
                              ))}
                            </div>
                          )}
                          {doc.ai_analysis.risk_flags?.length > 0 && (
                            <div>
                              <div className="text-xs uppercase text-orange-600 mb-2">Risk Flags</div>
                              {doc.ai_analysis.risk_flags.map((f, i) => (
                                <div key={i} className="text-sm text-[#333] flex gap-2 mb-1"><AlertTriangle className="h-3.5 w-3.5 text-orange-500 mt-0.5 shrink-0" /> {f}</div>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
